import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Company {
  id: number;
  name: string;
  company_name?: string;
}

interface SlackIntegration {
  id: number;
  slack_team_id: string;
  slack_team_name: string | null;
  company_id: number;
  company_name?: string;
  company_contact_name?: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isGlobalAdmin = !!(user?.role === 'admin' && !user?.company_id);
  const [slackIntegrations, setSlackIntegrations] = useState<SlackIntegration[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [slackLoading, setSlackLoading] = useState(false);
  const [showSlackModal, setShowSlackModal] = useState(false);
  const [slackForm, setSlackForm] = useState({ slack_team_id: '', slack_team_name: '', company_id: '' });
  const [slackSaving, setSlackSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setMfaEnabled(user.mfa_enabled || false);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    const fetchSlack = async () => {
      setSlackLoading(true);
      try {
        const [intRes, compRes] = await Promise.all([
          api.get<SlackIntegration[]>('/slack/integrations'),
          api.get<Company[]>('/companies'),
        ]);
        setSlackIntegrations(intRes.data);
        setCompanies(compRes.data);
      } catch (e) {
        console.error('Fetch Slack integrations / companies:', e);
      } finally {
        setSlackLoading(false);
      }
    };
    fetchSlack();
  }, [isGlobalAdmin]);

  const handleSendVerificationCode = async () => {
    setSendingCode(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/send-code');
      setSuccess('Verification code sent to your email');
      setShowEnableModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleEnableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/enable', { code: verificationCode });
      setMfaEnabled(true);
      setShowEnableModal(false);
      setVerificationCode('');
      setSuccess('MFA enabled successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to enable MFA');
    }
  };

  const handleDisableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await api.post('/auth/mfa/disable', { password });
      setMfaEnabled(false);
      setShowDisableModal(false);
      setPassword('');
      setSuccess('MFA disabled successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    }
  };

  const handleAddSlackIntegration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSlackSaving(true);
    try {
      await api.post('/slack/integrations', {
        slack_team_id: slackForm.slack_team_id.trim(),
        slack_team_name: slackForm.slack_team_name.trim() || undefined,
        company_id: parseInt(slackForm.company_id, 10),
      });
      setShowSlackModal(false);
      setSlackForm({ slack_team_id: '', slack_team_name: '', company_id: '' });
      setSuccess('Slack integration added');
      const intRes = await api.get<SlackIntegration[]>('/slack/integrations');
      setSlackIntegrations(intRes.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add Slack integration');
    } finally {
      setSlackSaving(false);
    }
  };

  const handleDeleteSlackIntegration = async (id: number) => {
    if (!window.confirm('Remove this Slack integration?')) return;
    setError('');
    setSuccess('');
    try {
      await api.delete(`/slack/integrations/${id}`);
      setSuccess('Slack integration removed');
      setSlackIntegrations((prev) => prev.filter((s) => s.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove integration');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Multi-Factor Authentication</h2>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {mfaEnabled 
                  ? 'MFA is currently enabled. You will need to enter a verification code when logging in.'
                  : 'MFA is currently disabled. Enable it to add an extra layer of security to your account.'}
              </p>
            </div>
            <div className="ml-4">
              {mfaEnabled ? (
                <button
                  onClick={() => setShowDisableModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Disable MFA
                </button>
              ) : (
                <button
                  onClick={handleSendVerificationCode}
                  disabled={sendingCode}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {sendingCode ? 'Sending...' : 'Enable MFA'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isGlobalAdmin && (
        <div className="mt-8 bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Slack integration</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Map Slack workspaces to NxtPortal companies so users can create and update tickets via <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/nxtportal</code> slash commands.
            </p>
            {slackLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Workspace → Company</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSlackModal(true);
                      setError('');
                      setSuccess('');
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                  >
                    Add integration
                  </button>
                </div>
                {slackIntegrations.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No Slack integrations yet. Add one to enable <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/nxtportal create</code> and <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/nxtportal update</code>.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {slackIntegrations.map((s) => (
                      <li key={s.id} className="py-3 flex items-center justify-between">
                        <div>
                          <span className="font-mono text-sm text-gray-700 dark:text-gray-300">{s.slack_team_id}</span>
                          {s.slack_team_name && <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({s.slack_team_name})</span>}
                          <span className="mx-2 text-gray-400">→</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{s.company_name || s.company_contact_name || `Company #${s.company_id}`}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteSlackIntegration(s.id)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Slack integration modal */}
      {showSlackModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowSlackModal(false)} />
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddSlackIntegration}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">Add Slack integration</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Slack Team ID</label>
                      <input
                        type="text"
                        required
                        placeholder="T01234567"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.slack_team_id}
                        onChange={(e) => setSlackForm((f) => ({ ...f, slack_team_id: e.target.value }))}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Find it in Slack: Open Slack in a browser, the Team ID is in the URL (e.g., https://app.slack.com/client/T0123...) starting with &apos;T&apos;.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Workspace name (optional)</label>
                      <input
                        type="text"
                        placeholder="My Workspace"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.slack_team_name}
                        onChange={(e) => setSlackForm((f) => ({ ...f, slack_team_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                      <select
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                        value={slackForm.company_id}
                        onChange={(e) => setSlackForm((f) => ({ ...f, company_id: e.target.value }))}
                      >
                        <option value="">Select company</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.company_name || c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={slackSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {slackSaving ? 'Saving...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSlackModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enable MFA Modal */}
      {showEnableModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowEnableModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEnableMfa}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Enable Multi-Factor Authentication
                  </h3>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      A verification code has been sent to your email. Enter the code below to enable MFA.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-widest"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Enable MFA
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEnableModal(false);
                      setVerificationCode('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Disable MFA Modal */}
      {showDisableModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowDisableModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleDisableMfa}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Disable Multi-Factor Authentication
                  </h3>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Please enter your password to confirm you want to disable MFA.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                      <input
                        type="password"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 dark:bg-red-500 text-base font-medium text-white hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Disable MFA
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDisableModal(false);
                      setPassword('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
